import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

/* EO1 and EO3 are rendered by the shared drawer, so a row has to be reshaped
   into the queue-item form those screens expect. */
const toDrawerItem = (row) =>
  row
    ? {
        id: row.status === "not_offered" ? "EO1" : "EO3",
        orderId: row.orderId,
        orderNumber: row.orderNumber,
        clinicName: row.clinicName,
        context: {
          slotsOfferedDaysAgo: row.slotsOfferedAt
            ? Math.floor(
                (Date.now() - new Date(row.slotsOfferedAt).getTime()) /
                  86400000,
              )
            : null,
        },
      }
    : null;

/* Palette and metrics taken from the internal-dash prototype */
/* Shared palette, so this page follows the light and dark toggle too. */

const STATUS_META = {
  not_offered: { label: "Slots not offered", bg: UI.amberBg, fg: UI.amber },
  offered_no_response: { label: "No response", bg: UI.redBg, fg: UI.red },
  booked: { label: "Booked", bg: UI.blueBg, fg: UI.blue },
  done: { label: "Done", bg: UI.greenBg, fg: UI.green },
};

const Tag = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status || "—",
    bg: UI.bg3,
    fg: UI.ink3,
  };
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11,
        padding: "2px 7px",
        borderRadius: "3px",
        fontWeight: 500,
        letterSpacing: ".2px",
        whiteSpace: "nowrap",
        bgcolor: meta.bg,
        color: meta.fg,
      }}
    >
      {meta.label}
    </Box>
  );
};

/* "8 Aug, 11:00" */
const formatBookedFor = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day}, ${time}`;
};

const sameDay = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

/* Monday to Saturday of the week `weekOffset` weeks from the current one. */
const weekDaysFor = (weekOffset) => {
  const today = new Date();
  const monday = new Date(today);
  const offset = (today.getDay() + 6) % 7; // Sunday = 6
  monday.setDate(today.getDate() - offset + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);

  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const PAGE_SIZE = 15;

const OnboardingCalls = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const [offerRow, setOfferRow] = useState(null);
  const [chaseRow, setChaseRow] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  // `search` is what is typed; `searchTerm` is what is sent, a moment after typing stops.
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCalls = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchOnboardingCalls(token, {
        page: targetPage,
        limit: PAGE_SIZE,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchTerm || undefined,
      });
      // The calendar strip needs every booked call for the week, not just the
      // current page — `data` is the full, unpaginated set; `tableRows` is
      // the paginated (and status-filtered) slice the table below renders.
      setRows(res.data || []);
      setTableRows(res.tableRows || []);
      setPagination(res.pagination || null);
      setSummary(res.summary || null);
      setKpis(res.kpis || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching onboarding calls");
    } finally {
      setIsLoading(false);
    }
  };

  // Changing the filter starts back at page 1 rather than keeping whatever
  // page number happened to be showing under the old filter.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchCalls(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, searchTerm]);

  // Push the updated row back into the list rather than refetching.
  const applyUpdate = (updated) => {
    if (!updated) {
      fetchCalls();
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.orderId === updated.orderId ? { ...r, ...updated } : r)),
    );
  };

  const days = weekDaysFor(weekOffset);
  const today = new Date();
  const isCurrentWeek = weekOffset === 0;
  const weekRangeLabel = `${days[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${days[
    days.length - 1
  ].toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;

  const kpiCards = [
    { label: "Needs your action", value: kpis?.needsAction },
    { label: "Blocking", value: kpis?.blocking, alarm: true },
    { label: "Accounts owned", value: kpis?.accountsOwned },
    { label: "Calls this week", value: kpis?.callsThisWeek },
  ];

  const columns = ["Clinic", "Doctor", "Owner", "Status", "Booked for", "Action"];

  const FILTER_OPTIONS = [
    { key: "all", label: "All" },
    { key: "not_offered", label: "Not offered" },
    { key: "offered_no_response", label: "No response" },
    { key: "booked", label: "Booked" },
    { key: "done", label: "Done" },
  ];

  // Status filtering, the "not_offered always first" ordering, and pagination
  // now all happen server-side (order-onboarding.service.js) — tableRows
  // arrives already filtered, sorted and sliced to the current page.

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      {/* page head */}
      <Box
        component="h1"
        sx={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px", color: UI.ink }}
      >
        Onboarding calls
      </Box>
      <Box
        sx={{ color: UI.ink3, margin: "0 0 22px", fontSize: 13, maxWidth: 760 }}
      >
        Cases assigned to you plus every live account you own. Payment detail is
        restricted.
      </Box>

      {/* kpis */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: "12px",
          marginBottom: "26px",
        }}
      >
        {kpiCards.map((k) => (
          <Box
            key={k.label}
            sx={{
              bgcolor: UI.surface,
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "8px",
              padding: "14px 16px",
            }}
          >
            <Box
              sx={{
                fontSize: 26,
                fontWeight: 500,
                lineHeight: 1.1,
                color: k.alarm ? UI.red : UI.ink,
              }}
            >
              {k.value === undefined || k.value === null ? "—" : k.value}
            </Box>
            <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "3px" }}>
              {k.label}
            </Box>
          </Box>
        ))}
      </Box>

      {/* section head */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
          margin: "0 0 4px",
        }}
      >
        <Box sx={{ fontSize: 15, fontWeight: 500, color: UI.ink }}>
          Onboarding calls
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            component="button"
            type="button"
            onClick={() => setWeekOffset((w) => w - 1)}
            sx={{
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "4px",
              bgcolor: UI.surface,
              color: UI.ink,
              fontSize: 12,
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            ‹ Previous
          </Box>
          <Box sx={{ fontSize: 12, color: UI.ink3, minWidth: 90, textAlign: "center" }}>
            {weekRangeLabel}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setWeekOffset((w) => w + 1)}
            sx={{
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "4px",
              bgcolor: UI.surface,
              color: UI.ink,
              fontSize: 12,
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Next ›
          </Box>
          {!isCurrentWeek && (
            <Box
              component="button"
              type="button"
              onClick={() => setWeekOffset(0)}
              sx={{
                border: "none",
                bgcolor: "transparent",
                color: UI.p,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                textDecoration: "underline",
              }}
            >
              Today
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ fontSize: 12, color: UI.ink3, margin: "0 0 14px" }}>
        {isCurrentWeek
          ? "This week, plus everything offered and unanswered."
          : "Booked calls for this week. \"Slots to offer\" only ever shows on today."}
      </Box>

      {/* week strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(6, 1fr)" },
          gap: "10px",
          marginBottom: "28px",
        }}
      >
        {days.map((day) => {
          const booked = rows.filter(
            (r) =>
              r.bookedFor &&
              (r.status === "booked" || r.status === "done") &&
              sameDay(new Date(r.bookedFor), day),
          );
          // Anything still awaiting an offer surfaces on today's column.
          const toOffer = sameDay(day, today)
            ? rows.filter((r) => r.status === "not_offered")
            : [];

          return (
            <Box
              key={day.toISOString()}
              sx={{
                bgcolor: UI.surface,
                border: "1px solid",
                borderColor: UI.line,
                borderRadius: "8px",
                minHeight: 150,
              }}
            >
              <Box
                sx={{
                  padding: "9px 11px",
                  borderBottom: "1px solid",
                  borderBottomColor: UI.line,
                  bgcolor: UI.bg2,
                  fontSize: 12,
                  fontWeight: 500,
                  color: UI.ink,
                }}
              >
                {day.toLocaleDateString("en-IN", { weekday: "short" })}
                <Box
                  component="small"
                  sx={{
                    display: "block",
                    color: UI.ink3,
                    fontWeight: 400,
                    fontSize: 11,
                  }}
                >
                  {day.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </Box>
              </Box>
              <Box sx={{ padding: "8px" }}>
                {booked.length === 0 && toOffer.length === 0 ? (
                  <Box sx={{ fontSize: 11, color: UI.ink4 }}>No calls</Box>
                ) : (
                  <React.Fragment>
                    {booked.map((r) => (
                      <Box
                        key={`b-${r.orderId}`}
                        onClick={() => navigate(`/dashboard/orders/${r.orderId}`)}
                        sx={{
                          bgcolor: UI.p50,
                          borderLeft: "2px solid",
                          borderLeftColor: UI.p,
                          borderRadius: "0 4px 4px 0",
                          padding: "7px 9px",
                          marginBottom: "7px",
                          fontSize: 11,
                          cursor: "pointer",
                          "&:hover": { bgcolor: UI.p100 },
                        }}
                      >
                        <Box sx={{ fontWeight: 500, color: UI.pDark }}>
                          {formatBookedFor(r.bookedFor).split(", ")[1]}{" "}
                          {r.doctorName}
                        </Box>
                        <Box sx={{ color: UI.ink3, marginTop: "2px" }}>
                          {r.clinicName}
                          {r.callLengthMins ? `, ${r.callLengthMins} min` : ""}
                        </Box>
                      </Box>
                    ))}
                    {toOffer.map((r) => (
                      <Box
                        key={`o-${r.orderId}`}
                        onClick={() => setOfferRow(r)}
                        sx={{
                          bgcolor: UI.amberBg,
                          borderLeft: "2px solid",
                          borderLeftColor: UI.amber,
                          borderRadius: "0 4px 4px 0",
                          padding: "7px 9px",
                          marginBottom: "7px",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                      >
                        <Box sx={{ fontWeight: 500, color: UI.amber }}>
                          Slots to offer
                        </Box>
                        <Box sx={{ color: UI.ink3, marginTop: "2px" }}>
                          {r.doctorName || r.clinicName}
                        </Box>
                      </Box>
                    ))}
                  </React.Fragment>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* search */}
      <Box
        component="input"
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search clinic, doctor or owner"
        aria-label="Search onboarding calls"
        sx={{
          display: "block",
          width: "100%",
          maxWidth: 420,
          marginBottom: "12px",
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          bgcolor: UI.surface,
          color: UI.ink,
          fontSize: 13,
          padding: "8px 12px",
          fontFamily: "inherit",
          outline: "none",
          "&:focus": { borderColor: UI.p },
        }}
      />

      {/* status filter */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
        {FILTER_OPTIONS.map((f) => {
          const active = statusFilter === f.key;
          return (
            <Box
              component="button"
              type="button"
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              sx={{
                border: "1px solid",
                borderColor: active ? UI.p : UI.line,
                borderRadius: "999px",
                bgcolor: active ? UI.p50 : UI.surface,
                color: active ? UI.p : UI.ink3,
                fontSize: 12,
                fontWeight: 500,
                padding: "5px 12px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {f.label}
            </Box>
          );
        })}
      </Box>

      {/* table */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          overflow: "hidden",
          marginBottom: "28px",
          bgcolor: UI.surface,
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box
            component="table"
            sx={{ width: "100%", borderCollapse: "collapse" }}
          >
            <Box component="thead">
              <Box component="tr">
                {columns.map((c) => (
                  <Box
                    component="th"
                    key={c}
                    sx={{
                      textAlign: "left",
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: ".5px",
                      color: UI.ink3,
                      fontWeight: 500,
                      padding: "10px 14px",
                      bgcolor: UI.bg2,
                      borderBottom: "1px solid",
                      borderBottomColor: UI.line,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {tableRows.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={columns.length}>
                    <Box
                      sx={{
                        padding: "26px",
                        textAlign: "center",
                        color: UI.ink3,
                        fontSize: 13,
                      }}
                    >
                      {loadFailed
                        ? "Could not load onboarding calls."
                        : searchTerm
                          ? "No calls match your search."
                          : "Nothing here yet."}
                      {loadFailed && (
                        <Box
                          component="button"
                          type="button"
                          onClick={fetchCalls}
                          sx={{
                            display: "block",
                            margin: "10px auto 0",
                            bgcolor: UI.surface,
                            color: UI.p,
                            border: "1px solid",
                            borderColor: UI.p100,
                            borderRadius: "4px",
                            padding: "6px 12px",
                            fontSize: 13,
                            fontWeight: 500,
                            fontFamily: "inherit",
                            cursor: "pointer",
                          }}
                        >
                          Retry
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                tableRows.map((r) => (
                  <Box
                    component="tr"
                    key={r.orderId}
                    onClick={() => navigate(`/dashboard/orders/${r.orderId}`)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: UI.p50 },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <Box component="td" sx={cellSx}>
                      {r.clinicName || "—"}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      {r.doctorName || "—"}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      {r.ownerName || "—"}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      <Tag status={r.status} />
                    </Box>
                    <Box component="td" sx={cellSx}>
                      {formatBookedFor(r.bookedFor)}
                    </Box>
                    <Box component="td" sx={cellSx}>
                      {r.status === "not_offered" ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOfferRow(r);
                          }}
                          sx={primaryBtnSx}
                        >
                          Offer slots
                        </Box>
                      ) : r.status === "offered_no_response" ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChaseRow(r);
                          }}
                          sx={primaryBtnSx}
                        >
                          Follow up
                        </Box>
                      ) : (
                        "—"
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>

        {summary && (
          <Box
            sx={{
              padding: "10px 14px",
              bgcolor: UI.bg2,
              borderTop: "1px solid",
              borderTopColor: UI.line,
              fontSize: 12,
              color: UI.ink3,
            }}
          >
            {summary.awaitingOffer} awaiting slot offer ·{" "}
            {summary.offeredNoResponse} offered with no response ·{" "}
            {summary.booked} booked
          </Box>
        )}
      </Box>

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "18px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {summary?.offeredNoResponse > 0 && (
        <Box
          sx={{
            bgcolor: UI.amberBg,
            border: "1px solid",
            borderColor: UI.amberLine,
            color: UI.amberInk,
            fontSize: 12,
            padding: "10px 13px",
            borderRadius: "4px",
            marginBottom: "18px",
          }}
        >
          <b style={{ fontWeight: 500 }}>Open decision.</b> Chase cadence for an
          unselected slot is undefined, so nothing chases these automatically.
        </Box>
      )}

      <ActionDrawer
        open={Boolean(offerRow || chaseRow)}
        screenKey={offerRow ? "offer-slots" : "chase-slot"}
        item={toDrawerItem(offerRow || chaseRow)}
        onClose={() => {
          setOfferRow(null);
          setChaseRow(null);
        }}
        onSaved={(updated) => {
          setOfferRow(null);
          setChaseRow(null);
          applyUpdate(updated);
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

const cellSx = {
  padding: "11px 14px",
  borderBottom: "1px solid",
  borderBottomColor: UI.line,
  fontSize: 13,
  verticalAlign: "top",
  color: UI.ink,
};

const primaryBtnSx = {
  bgcolor: UI.p,
  color: UI.surface,
  border: 0,
  borderRadius: "4px",
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: ".2px",
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: UI.pDark },
};

export default OnboardingCalls;
